import { BLOCK, CLIENT_EVENTS, AWAIT, LOCAL_ACTIONS } from "./agentContract";
import {
  COMPANY_COLUMNS,
  PROSPECT_COLUMNS,
  getTotalCount,
  removeByTitleMatch,
  SALES_DEV_MATCH
} from "./mockProspects";

/**
 * The scripted demo conversation the mock transport replays. Each step:
 *   { id, expects(clientEvent, ctx) -> bool, run(api, clientEvent, ctx) }
 * `api` comes from MockAgentTransport: { beginTurn, wait, ctx }.
 * Free-text intents match loose regexes; structured events match on
 * replyId / actionId / confirmId. run() returning false keeps the step
 * available for a retry (used by the OAuth beat).
 */

const isText = evt => evt.type === CLIENT_EVENTS.USER_MESSAGE;
const isReply = (evt, replyId) =>
  evt.type === CLIENT_EVENTS.QUICK_REPLY_SELECTED && evt.replyId === replyId;
const isCta = (evt, actionId) =>
  evt.type === CLIENT_EVENTS.CTA_CLICKED && evt.actionId === actionId;
// People answer chips by typing too ("Both are fine", "Yes", "Lead"). Each
// choice beat accepts either; when the answer came as text we patch the
// quick-reply block's selectedValue so the chips render as answered.
const textMatches = (evt, regex) => isText(evt) && regex.test(evt.text);
const markAnswered = (turn, blockId, value) => {
  if (blockId) turn.patch(blockId, { selectedValue: value });
};

// progress helper: open a running progress block, hold it for `duration`,
// then flip it to done with an optional result detail.
const runProgress = async (api, turn, { label, messages, duration = 2400, detail }) => {
  const blockId = turn.openBlock(BLOCK.PROGRESS, {
    status: "running",
    label,
    messages
  });
  await api.wait(duration);
  turn.patch(blockId, { status: "done", detail });
  turn.closeBlock(blockId);
  return blockId;
};

const buildScenario = () => [
  // Beat 1: the opening research ask, answered with a clarifying question.
  {
    id: "find_companies",
    expects: evt =>
      isText(evt) && /(hr\s*tech|saas|funding|100\s*m|companies)/i.test(evt.text),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "Sure, I can research that. Before I start, one thing I'd like to flag: are you looking for companies that have a SaaS plus services offering, or purely a SaaS offering?"
      );
      ctx.offeringBlockId = turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Which offering type should I include?",
        replyId: "offering_type",
        options: [
          { value: "both", label: "Both are fine" },
          { value: "saas_only", label: "Pure SaaS only" }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
    }
  },

  // Beat 2: research runs, companies land with a live table + CTAs.
  {
    id: "companies_result",
    expects: (evt, ctx) =>
      isReply(evt, "offering_type") ||
      (Boolean(ctx.offeringBlockId) &&
        textMatches(evt, /\b(both|fine|pure|only)\b/i)),
    run: async (api, evt, ctx) => {
      const value =
        evt.value || (/\b(pure|only)\b/i.test(evt.text) ? "saas_only" : "both");
      ctx.offeringType = value;
      const turn = api.beginTurn();
      if (isText(evt)) markAnswered(turn, ctx.offeringBlockId, value);
      await turn.text(
        value === "saas_only"
          ? "Got it, pure SaaS companies only."
          : "Got it, I'll include both."
      );
      await runProgress(api, turn, {
        label: "Researching companies",
        messages: [
          "Scanning HR tech and SaaS companies…",
          "Checking funding rounds above $100M…",
          "Cross-referencing firmographics…",
          "Building your company list…"
        ],
        duration: 2800,
        detail: "128 companies matched"
      });
      await turn.text(
        "Here's your list: **128 HR tech and SaaS companies** that raised **$100M or more**. I've included funding round and headquarters so you can scan them quickly."
      );
      ctx.companyTableBlockId = turn.addBlock(BLOCK.TABLE_REF, {
        tableId: "tbl_companies",
        title: "HR tech and SaaS companies, $100M+ funding",
        totalCount: getTotalCount("tbl_companies"),
        columns: COMPANY_COLUMNS
      });
      turn.addBlock(BLOCK.CTA_CHIPS, {
        chips: [
          {
            actionId: LOCAL_ACTIONS.VIEW_TABLE,
            tableId: "tbl_companies",
            label: "View live table",
            icon: "grid_on"
          },
          {
            actionId: "add_to_accounts",
            label: "Add to Salesgear accounts",
            style: "primary",
            icon: "domain"
          }
        ]
      });
      turn.end(AWAIT.CTA);
    }
  },

  // Beat 3: add the companies as accounts, then offer the next move.
  {
    id: "add_accounts",
    expects: (evt, ctx) =>
      isCta(evt, "add_to_accounts") ||
      (Boolean(ctx.companyTableBlockId) &&
        textMatches(evt, /\badd\b.*\b(account|salesgear|them|these)\b/i)),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await runProgress(api, turn, {
        label: "Adding accounts",
        messages: [
          "Creating 128 accounts in Salesgear…",
          "De-duplicating against existing accounts…",
          "Tagging them as HR tech and SaaS…"
        ],
        duration: 2000,
        detail: "128 accounts added"
      });
      await turn.text(
        "Done, all **128 companies** are now in your Salesgear accounts. Shall we find decision makers on these accounts?"
      );
      ctx.findDmBlockId = turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Find decision makers?",
        replyId: "find_dm",
        options: [
          { value: "yes", label: "Sure, let's do it" },
          { value: "not_now", label: "Not now" }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
    }
  },

  // Beat 4: persona picker driven by the seller profile.
  {
    id: "persona_pick",
    expects: (evt, ctx) =>
      (isReply(evt, "find_dm") && evt.value === "yes") ||
      (Boolean(ctx.findDmBlockId) &&
        textMatches(evt, /^(yes|yeah|yep|sure|ok(ay)?|let'?s|go ahead)\b/i)),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      if (isText(evt)) markAnswered(turn, ctx.findDmBlockId, "yes");
      await turn.text(
        "Based on your seller profile, these are the personas that best fit your business. Pick the most relevant:"
      );
      ctx.personaBlockId = turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Which persona should I target?",
        replyId: "persona",
        options: [
          {
            value: "cxo_vp",
            label: "CXOs and VPs in Marketing and Sales",
            description: "Economic buyers who own the revenue number"
          },
          {
            value: "demand_bizdev",
            label: "Demand Gen Managers and VPs of Business Development",
            description: "Hands-on pipeline builders"
          },
          {
            value: "revops",
            label: "RevOps and Sales Ops",
            description: "Own the tooling and process decisions"
          }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
    }
  },

  // Beat 5: prospect search returns 3,210 people with a live table.
  {
    id: "prospect_search",
    expects: (evt, ctx) =>
      isReply(evt, "persona") ||
      (Boolean(ctx.personaBlockId) &&
        textMatches(evt, /cxo|vp|marketing|demand|biz\s*dev|revops|sales\s*ops/i)),
    run: async (api, evt, ctx) => {
      const value =
        evt.value ||
        (/revops|sales\s*ops/i.test(evt.text)
          ? "revops"
          : /demand|biz\s*dev/i.test(evt.text)
          ? "demand_bizdev"
          : "cxo_vp");
      ctx.persona = value;
      const turn = api.beginTurn();
      if (isText(evt)) markAnswered(turn, ctx.personaBlockId, value);
      await runProgress(api, turn, {
        label: "Searching prospects",
        messages: [
          "Searching across your 128 target accounts…",
          "Matching titles to your persona…",
          "Verifying emails and direct dials…",
          "Ranking by fit…"
        ],
        duration: 3200,
        detail: "3,210 prospects found"
      });
      await turn.text(
        "Found **3,210 potential prospects** matching that persona across your 128 target accounts."
      );
      ctx.prospectTableBlockId = turn.addBlock(BLOCK.TABLE_REF, {
        tableId: "tbl_prospects",
        title: "Decision makers at your target accounts",
        totalCount: getTotalCount("tbl_prospects"),
        columns: PROSPECT_COLUMNS
      });
      turn.addBlock(BLOCK.CTA_CHIPS, {
        chips: [
          {
            actionId: LOCAL_ACTIONS.VIEW_TABLE,
            tableId: "tbl_prospects",
            label: "View table",
            icon: "grid_on"
          },
          {
            actionId: "add_to_list",
            label: "Add to list",
            style: "primary",
            icon: "playlist_add"
          }
        ]
      });
      turn.end(AWAIT.CTA);
    }
  },

  // Beat 6: "some of these titles are wrong" -> ask for specifics.
  {
    id: "titles_wrong",
    expects: evt =>
      isText(evt) && /(title|wrong|incorrect|issue|mistake)/i.test(evt.text),
    run: async api => {
      const turn = api.beginTurn();
      await turn.text(
        "Can you point me to the titles that have issues? Open the live table and tell me a name or a title pattern, and I'll verify it before making any changes."
      );
      turn.end(
        AWAIT.USER_INPUT,
        'e.g. "Adam\'s title is wrong, it says Sales Development"'
      );
    }
  },

  // Beat 7: the Adam correction -> explain, then ask for confirmation.
  {
    id: "adam_correction",
    expects: evt =>
      isText(evt) &&
      /(adam|sales\s*development|business\s*development)/i.test(evt.text),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "Got it. One thing worth knowing: **Sales Development** and **Business Development** are usually the same job function, the terminology just differs from company to company. Do you still want me to remove all prospects with Sales Development titles from your target list?"
      );
      ctx.confirmBlockId = turn.addBlock(BLOCK.CONFIRMATION, {
        confirmId: "remove_sales_dev",
        prompt: "Remove every prospect with a Sales Development title?",
        confirmLabel: "Yes, remove them",
        cancelLabel: "Keep them"
      });
      turn.end(AWAIT.CONFIRMATION);
    }
  },

  // Beat 8: apply (or skip) the removal, sync the table count.
  {
    id: "remove_confirmed",
    expects: (evt, ctx) =>
      (evt.type === CLIENT_EVENTS.CONFIRMATION_RESPONSE &&
        evt.confirmId === "remove_sales_dev") ||
      (Boolean(ctx.confirmBlockId) &&
        textMatches(evt, /^(yes|yeah|yep|sure|ok(ay)?|confirm|remove|no|keep|cancel)\b/i)),
    run: async (api, evt, ctx) => {
      const confirmed = isText(evt)
        ? !/^(no|keep|cancel)\b/i.test(evt.text)
        : evt.confirmed;
      const turn = api.beginTurn();
      if (isText(evt) && ctx.confirmBlockId) {
        turn.patch(ctx.confirmBlockId, {
          resolution: confirmed ? "confirmed" : "cancelled"
        });
      }
      if (!confirmed) {
        await turn.text("No problem, I'll keep them in the list.");
        turn.end(AWAIT.USER_INPUT);
        return;
      }
      await runProgress(api, turn, {
        label: "Removing Sales Development titles",
        messages: [
          "Verifying before making any changes…",
          "Matching Sales Development titles…",
          "Updating your target list…"
        ],
        duration: 2000,
        detail: "210 prospects removed"
      });
      const removed = removeByTitleMatch("tbl_prospects", SALES_DEV_MATCH);
      const remaining = getTotalCount("tbl_prospects");
      if (ctx.prospectTableBlockId) {
        turn.patch(ctx.prospectTableBlockId, { totalCount: remaining });
      }
      await turn.text(
        `Removed **${removed.toLocaleString()} prospects** with Sales Development titles. Your prospect list is now **${remaining.toLocaleString()} contacts**.`
      );
      turn.addBlock(BLOCK.CTA_CHIPS, {
        chips: [
          {
            actionId: LOCAL_ACTIONS.VIEW_TABLE,
            tableId: "tbl_prospects",
            label: "View table",
            icon: "grid_on"
          },
          {
            actionId: "add_to_list",
            label: "Add to list",
            style: "primary",
            icon: "playlist_add"
          }
        ]
      });
      turn.end(AWAIT.CTA);
    }
  },

  // Beat 9: export intent -> pick a CRM.
  {
    id: "export_intent",
    expects: evt => isText(evt) && /(export|crm|salesforce|hubspot|zoho)/i.test(evt.text),
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      await turn.text(
        "Sure. To export, you'll need a connected CRM. Which CRM would you like to export to?"
      );
      ctx.crmChoiceBlockId = turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Pick your CRM",
        replyId: "crm_choice",
        options: [
          { value: "salesforce", label: "Salesforce" },
          { value: "hubspot", label: "HubSpot" },
          { value: "zoho", label: "Zoho" }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
    }
  },

  // Beat 10: Salesforce isn't connected -> connect action.
  {
    id: "crm_choice",
    expects: (evt, ctx) =>
      isReply(evt, "crm_choice") ||
      (Boolean(ctx.crmChoiceBlockId) &&
        textMatches(evt, /^(salesforce|hubspot|zoho)\b/i)),
    run: async (api, evt, ctx) => {
      const value = evt.value || evt.text.trim().toLowerCase().split(/\s/)[0];
      const turn = api.beginTurn();
      if (isText(evt)) markAnswered(turn, ctx.crmChoiceBlockId, value);
      if (value !== "salesforce") {
        await turn.text(
          "For this demo, let's go with **Salesforce**. The flow is identical for the other CRMs."
        );
      }
      await turn.text(
        "Your Salesforce isn't connected yet. Please connect it and then I can initiate the export."
      );
      ctx.connectBlockId = turn.addBlock(BLOCK.CONNECT_INTEGRATION, {
        provider: "salesforce",
        actionId: "connect_salesforce",
        label: "Connect Salesforce",
        description: "Opens the Salesforce login window. I'll pick up right where we left off once you're connected.",
        status: "pending"
      });
      turn.end(AWAIT.ACTION_RESULT);
    }
  },

  // Beat 11: OAuth result re-enters the loop. Stays available on failure.
  {
    id: "oauth_result",
    expects: evt =>
      evt.type === CLIENT_EVENTS.ACTION_RESULT &&
      evt.actionId === "connect_salesforce",
    run: async (api, evt, ctx) => {
      const turn = api.beginTurn();
      if (evt.status !== "success") {
        if (ctx.connectBlockId) {
          turn.patch(ctx.connectBlockId, { status: "failed" });
        }
        await turn.text(
          "Looks like the Salesforce window closed before the connection finished. Click **Connect Salesforce** above whenever you're ready."
        );
        turn.end(AWAIT.ACTION_RESULT);
        return false; // stay available for the retry
      }
      ctx.salesforceConnected = true;
      if (ctx.connectBlockId) {
        turn.patch(ctx.connectBlockId, { status: "connected" });
      }
      await runProgress(api, turn, {
        label: "Verifying connection status",
        messages: ["Checking Salesforce permissions…", "Confirming API access…"],
        duration: 1600,
        detail: "Salesforce connected"
      });
      await turn.text(
        "Connected. Starting the export. Do you want to export these prospects as **Leads** or **Contacts**?"
      );
      ctx.exportTypeBlockId = turn.addBlock(BLOCK.QUICK_REPLIES, {
        prompt: "Export as which record type?",
        replyId: "export_type",
        options: [
          { value: "lead", label: "Lead" },
          { value: "contact", label: "Contact" }
        ]
      });
      turn.end(AWAIT.QUICK_REPLY);
    }
  },

  // Beat 12: the export run, partial failure, and the plan-limit explanation.
  {
    id: "export_run",
    expects: (evt, ctx) =>
      isReply(evt, "export_type") ||
      (Boolean(ctx.exportTypeBlockId) &&
        textMatches(evt, /^(lead|contact)s?\b/i)),
    run: async (api, evt, ctx) => {
      const value =
        evt.value || (/^contact/i.test(evt.text.trim()) ? "contact" : "lead");
      ctx.exportType = value;
      const total = getTotalCount("tbl_prospects");
      const exported = 10;
      const failed = total - exported;
      const turn = api.beginTurn();
      if (isText(evt)) markAnswered(turn, ctx.exportTypeBlockId, value);
      await turn.text(
        `Sure, saving **${value === "lead" ? "Lead" : "Contact"}** as your preference for future exports.`
      );
      const progressId = turn.openBlock(BLOCK.PROGRESS, {
        status: "running",
        label: "Exporting to Salesforce",
        messages: [
          "Preparing records…",
          "Mapping Salesgear fields to Salesforce…",
          "Pushing batches…",
          "Checking sync results…"
        ],
        percent: 0
      });
      for (let percent = 10; percent <= 100; percent += 10) {
        await api.wait(380);
        turn.patch(progressId, { percent });
      }
      turn.patch(progressId, { status: "done", detail: "Export finished" });
      turn.closeBlock(progressId);
      turn.addBlock(BLOCK.SUMMARY_CARD, {
        title: "Salesforce export result",
        rows: [
          { label: "Prospects queued", value: total.toLocaleString() },
          { label: "Exported", value: exported.toLocaleString(), tone: "success" },
          { label: "Failed to export", value: failed.toLocaleString(), tone: "danger" }
        ],
        footnote: `Exported as ${value === "lead" ? "Leads" : "Contacts"}`
      });
      await turn.text(
        `Only **${exported}** made it across, **${failed.toLocaleString()}** failed. There are two likely causes: either a Salesforce API limit was reached or your Salesgear plan limits exports. Let me check…`
      );
      await api.wait(1400);
      await turn.text(
        "Confirmed: your **Salesgear free trial** limits CRM exports to **10 records** during the trial phase. You can upgrade, or talk to support to get this resolved."
      );
      turn.addBlock(BLOCK.ERROR_CTA, {
        title: "Trial export limit reached",
        severity: "warn",
        markdown:
          "Your free trial allows **10 CRM exports**. Upgrade to unlock full exports, or talk to support and we'll sort it out.",
        chips: [
          {
            actionId: LOCAL_ACTIONS.NAVIGATE,
            path: "/pricing",
            label: "Upgrade plan",
            style: "primary",
            icon: "trending_up"
          },
          { actionId: LOCAL_ACTIONS.CONTACT_SUPPORT, label: "Talk to support", icon: "headset_mic" }
        ]
      });
      turn.end(AWAIT.USER_INPUT);
    }
  },

  // Support chip: Intercom opens client-side; the agent just acknowledges.
  {
    id: "talk_to_support",
    expects: evt => isCta(evt, LOCAL_ACTIONS.CONTACT_SUPPORT),
    run: async api => {
      const turn = api.beginTurn();
      await turn.text(
        "I've opened the support chat for you. Share this conversation's context and they'll take it from there."
      );
      turn.end(AWAIT.USER_INPUT);
    }
  }
];

export default buildScenario;
